import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Login.module.css';
import ErrorComponent from './error/ErrorComponent';
import { HOST } from '../config/api';
import { storeShareInvite } from '../lib/shareInvite';

const Register = () => {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const location = useLocation();
    const shareInvite = location.state?.shareInvite || null;
    const prefilledRole = location.state?.prefilledRole === 'coach' ? 'coach' : 'user';
    const [role, setRole] = useState(prefilledRole);

    const roleMap = { coach: 99, user: 1 };
    const successBodyByRole = {
        coach:
            'We’ve sent you a verification email. After you verify your account, log in to create your first training plan, share it, and add your first training.',
        user:
            'We’ve sent you a verification email. After you verify your account, log in to browse coaches, subscribe to a plan, and open your first training.',
    };

    useEffect(() => {
        if (shareInvite?.code) {
            storeShareInvite(shareInvite);
        }
    }, [shareInvite]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage(null);
        setIsSubmitting(true);

        const payload = {
            email,
            firstName,
            lastName,
            password,
            role: roleMap[role],
        };

        if (password !== repeatPassword) {
            setErrorMessage('Passwords do not match');
            setIsSuccess(false);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch(HOST + '/v1/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                setErrorMessage(null);
                setMessage('Registration successful, please check your email.');
                setIsSuccess(true);
            } else {
                setErrorMessage(result.message);
                setIsSuccess(false);
            }
        } catch (error) {
            setErrorMessage('Registration failed. Please try again.');
            setIsSuccess(false);
            console.error('Error during registration:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.authPage}>
            <section className={styles.authShell}>
                <div className={styles.authIntro}>
                    <div className={styles.authImageWrap}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                        <div className={styles.authImageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Helping you achieve perfection.</strong>
                            <p>Start as a coach or as an athlete and move into the part of the product that fits your role.</p>
                        </div>
                    </div>

                    <div className={styles.authCopy}>
                        <span className={styles.authEyebrow}>Create account</span>
                        <h1>Join the system behind better training decisions.</h1>
                        <p>
                            Coaches can build plans and review athlete completion. Athletes can subscribe, train, and track
                            progress without friction.
                        </p>
                    </div>
                </div>

                <div className={styles.authPanel}>
                    {isSuccess ? (
                        <div className={styles.successPanel}>
                            <span className={styles.panelKicker}>Registration complete</span>
                            <h2>Check your email</h2>
                            <p className={styles.registrationMessageSuccess}>{message}</p>
                            <div className={styles.inviteNotice} role="status" aria-live="polite">
                                <span className={styles.inviteNoticeKicker}>{role === 'coach' ? 'Coach account' : 'Athlete account'}</span>
                                <p>{successBodyByRole[role]}</p>
                            </div>
                            {shareInvite?.message ? (
                                <div className={styles.inviteNotice} role="status" aria-live="polite">
                                    <span className={styles.inviteNoticeKicker}>Training invite</span>
                                    <p>
                                        {shareInvite.message} If you verify on this browser, we&apos;ll continue with the invite after login. If you verify elsewhere, reopen the invite link after logging in.
                                    </p>
                                </div>
                            ) : null}
                            <Link className={styles.primaryButton} to="/login" state={shareInvite ? { shareInvite } : undefined}>
                                Go to login after verification
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className={styles.panelHeader}>
                                <span className={styles.panelKicker}>Register</span>
                                <h2>Create your account</h2>
                                <p>Choose your role and get into the app with a setup that feels focused from the start.</p>
                            </div>

                            {shareInvite?.message ? (
                                <div className={styles.inviteNotice} role="status" aria-live="polite">
                                    <span className={styles.inviteNoticeKicker}>Training invite</span>
                                    <p>{shareInvite.message}</p>
                                </div>
                            ) : null}

                            <form className={styles.authForm} onSubmit={handleSubmit}>
                                <div className={styles.formGroup}>
                                    <label className={styles.fieldLabel} htmlFor="email">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        className={styles.inputField}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>

                                <div className={styles.splitFields}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.fieldLabel} htmlFor="firstName">
                                            First name
                                        </label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            className={styles.inputField}
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="First name"
                                            required
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.fieldLabel} htmlFor="lastName">
                                            Last name
                                        </label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            className={styles.inputField}
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            placeholder="Last name"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.fieldLabel} htmlFor="password">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        className={styles.inputField}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Create a password"
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.fieldLabel} htmlFor="repeatPassword">
                                        Repeat password
                                    </label>
                                    <input
                                        type="password"
                                        id="repeatPassword"
                                        className={styles.inputField}
                                        value={repeatPassword}
                                        onChange={(e) => setRepeatPassword(e.target.value)}
                                        placeholder="Repeat your password"
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <span className={styles.fieldLabel}>Account type</span>
                                    <div className={styles.roleSwitch}>
                                        <label className={`${styles.roleOption} ${role === 'user' ? styles.roleOptionActive : ''}`}>
                                            <input
                                                type="radio"
                                                name="role"
                                                value="user"
                                                checked={role === 'user'}
                                                onChange={(e) => setRole(e.target.value)}
                                            />
                                            <span>Athlete</span>
                                        </label>
                                        <label className={`${styles.roleOption} ${role === 'coach' ? styles.roleOptionActive : ''}`}>
                                            <input
                                                type="radio"
                                                name="role"
                                                value="coach"
                                                checked={role === 'coach'}
                                                onChange={(e) => setRole(e.target.value)}
                                            />
                                            <span>Coach</span>
                                        </label>
                                    </div>
                                </div>

                                <ErrorComponent errorMessage={errorMessage} />

                                <div className={styles.actionStack}>
                                    <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>
                                        {isSubmitting ? 'Creating account...' : 'Register'}
                                    </button>
                                    <Link className={styles.secondaryButton} to="/login" state={shareInvite ? { shareInvite } : undefined}>
                                        I already have an account
                                    </Link>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Register;
